/**
 * BranchBroadcasts — shows venue-wide staff announcements ("Kitchen busy ~10 min", "Live music at 9")
 * as a dismissible banner at the top of the customer screen. Loads recent broadcasts (last 24h) on
 * mount, listens live on the branch Ably channel for new ones, and polls as a fallback. Dismissed
 * ids are remembered per device so a notice doesn't nag after it's been read.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Megaphone, X } from 'lucide-react';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';

interface Broadcast { id: string; message: string; createdByName: string | null; createdAt: string }

const DISMISS_KEY = 'cu_broadcasts_dismissed';

function loadDismissed(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]')); } catch { return new Set(); }
}
function saveDismissed(ids: Set<string>) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...ids].slice(-100))); } catch { /* ignore */ }
}

const BranchBroadcasts: React.FC<{ branchId?: string }> = ({ branchId }) => {
  const [items, setItems] = useState<Broadcast[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed());
  const seen = useRef<Set<string>>(new Set());

  const merge = (incoming: Broadcast[]) => {
    setItems((prev) => {
      const map = new Map(prev.map((b) => [b.id, b]));
      for (const b of incoming) { if (b?.id) map.set(b.id, b); }
      return [...map.values()].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    });
  };

  useEffect(() => {
    if (!branchId) return;
    let alive = true;
    apiService.getBranchBroadcasts(branchId).then((list) => { if (alive) merge(list); }).catch(() => {});

    const unsub = subscribeToChannel(
      `branch:${branchId}`,
      ['branch_broadcast:new'] as unknown as readonly string[],
      (_e, data) => { if (data?.id && !seen.current.has(data.id)) { seen.current.add(data.id); merge([data]); } },
    );
    const poll = setInterval(() => { apiService.getBranchBroadcasts(branchId).then((list) => merge(list)).catch(() => {}); }, 20000);

    return () => { alive = false; try { unsub(); } catch { /* noop */ } clearInterval(poll); };
  }, [branchId]);

  const visible = useMemo(() => items.filter((b) => !dismissed.has(b.id)).slice(0, 3), [items, dismissed]);
  if (!branchId || visible.length === 0) return null;

  const dismiss = (id: string) => {
    setDismissed((prev) => { const next = new Set(prev); next.add(id); saveDismissed(next); return next; });
  };

  const time = (iso: string) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

  return (
    <div style={wrap}>
      {visible.map((b) => (
        <div key={b.id} style={banner}>
          <Megaphone size={16} color="#fb923c" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={msg}>{b.message}</div>
            <div style={meta}>{b.createdByName || 'Staff'} · {time(b.createdAt)}</div>
          </div>
          <button type="button" onClick={() => dismiss(b.id)} style={closeBtn} aria-label="Dismiss"><X size={15} /></button>
        </div>
      ))}
    </div>
  );
};

const wrap: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8, margin: '0 0 10px' };
// Self-contained colours (legible on the dark guest screen; independent of page theme).
const banner: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 10, background: 'linear-gradient(90deg, rgba(249,115,22,0.22), rgba(249,115,22,0.10))', border: '1px solid rgba(249,115,22,0.55)', borderRadius: 12, padding: '10px 12px' };
const msg: React.CSSProperties = { fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.35, color: '#fde4cf', wordBreak: 'break-word' };
const meta: React.CSSProperties = { fontSize: '0.72rem', color: 'rgba(253,186,116,0.85)', marginTop: 2 };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: '#fdba74', cursor: 'pointer', opacity: 0.85, flexShrink: 0, lineHeight: 1 };

export default BranchBroadcasts;
